/**
 * Product Video Slider Component
 * Handles thumbnail slider and popup video slider with auto-play functionality
 */

import { Component } from '@theme/component';

class ProductVideoSlider extends Component {
  #isSoundOn = false;
  constructor() {
    super();
    this.thumbnailSwiper = null;
    this.popupSwiper = null;
    this.currentVideoIndex = 0;
  }

  connectedCallback() {
    super.connectedCallback();
    this.initializeThumbnailSwiper();
    this.setupThumbnailClickHandlers();
    this.setupDialogListeners();

    this.querySelectorAll('.sound-control').forEach((soundControl) => {
      soundControl.addEventListener('click', (e) => {
        e.preventDefault();
        const parentElement = soundControl.parentElement;
        if(!parentElement) return;
        const video = parentElement.querySelector('video');
        parentElement.classList.toggle('video-with-sound');
        if(video){
          video.muted = !video.muted;
          console.log('video', video.muted);
        }
        this.#isSoundOn = !this.#isSoundOn;
      });
    });
  }

  disconnectedCallback() {
    if (this.thumbnailSwiper) {
      this.thumbnailSwiper.destroy(true, true);
    }
    if (this.popupSwiper) {
      this.popupSwiper.destroy(true, true);
    }
    super.disconnectedCallback();
  }

  /**
   * Initialize thumbnail Swiper
   */
  initializeThumbnailSwiper() {
    if (typeof window.Swiper === 'undefined') {
      console.warn('Swiper not loaded. Retrying...');
      setTimeout(() => this.initializeThumbnailSwiper(), 100);
      return;
    }

    const thumbnailContainer = this.querySelector('.video-thumbnails-swiper');
    if (!thumbnailContainer) {
      return;
    }

    // Check if we have slides
    const slides = thumbnailContainer.querySelectorAll('.swiper-slide');
    if (slides.length === 0) {
      return;
    }

    try {
      this.thumbnailSwiper = new window.Swiper(thumbnailContainer, {
        slidesPerView: 3,
        spaceBetween: 10,
        
        freeMode: true,
        watchSlidesProgress: true,
        navigation: {
          nextEl: '.video-thumbnails-button-next',
          prevEl: '.video-thumbnails-button-prev',
        },
        breakpoints: {
          750: {
            spaceBetween: 16,
          },
        },
      });
    } catch (error) {
      console.error('Failed to initialize thumbnail Swiper:', error);
    }
  }

  /**
   * Setup click handlers for thumbnail buttons
   */
  setupThumbnailClickHandlers() {
    const thumbnailButtons = this.querySelectorAll('.video-thumbnail-button');
    thumbnailButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('button', e.target);
        const videoIndex = parseInt(button.dataset.videoIndex || button.closest('.swiper-slide').dataset.videoIndex, 10);
        console.log('videoIndex', videoIndex);
        this.openVideoPopup(videoIndex);
      });
    });
  }

  /**
   * Setup dialog event listeners
   */
  setupDialogListeners() {
    const dialogComponent = this.querySelector('dialog-component');
    if (!dialogComponent) {
      return;
    }

    // Listen for dialog open event
    dialogComponent.addEventListener('dialog:open', () => {
      // Small delay to ensure dialog is fully rendered
      setTimeout(() => {
        this.initializePopupSwiper();
      }, 100);
    });

    // Listen for dialog close event to pause videos
    dialogComponent.addEventListener('dialog:close', () => {
      this.pauseAllVideos();
    });
  }

  /**
   * Initialize popup Swiper
   */
  initializePopupSwiper() {
    if (typeof window.Swiper === 'undefined') {
      console.warn('Swiper not loaded. Retrying...');
      setTimeout(() => this.initializePopupSwiper(), 100);
      return;
    }

    const popupContainer = this.querySelector('.video-popup-swiper');
    if (!popupContainer) {
      return;
    }

    // Destroy existing swiper if it exists
    if (this.popupSwiper) {
      this.popupSwiper.destroy(true, true);
    }

    try {
      const component = this; // Store reference to component for use in callbacks
      this.popupSwiper = new window.Swiper(popupContainer, {
        slidesPerView: 1,
        spaceBetween: 0,
      
        initialSlide: this.currentVideoIndex,
        navigation: {
          nextEl: '.video-popup-button-next',
          prevEl: '.video-popup-button-prev',
        },
        on: {
          slideChange: () => {
            this.handlePopupSlideChange();
          },
          init: (swiper) => {
            // Ensure we're on the correct slide
            // swiper parameter is the Swiper instance
            if (this.currentVideoIndex > 0 && this.currentVideoIndex < swiper.slides.length) {
              swiper.slideTo(this.currentVideoIndex, 0);
            }
            // Play the initial video when popup opens
            setTimeout(() => {
              this.playCurrentVideo();
            }, 0);
          },
        },
      });
    } catch (error) {
      console.error('Failed to initialize popup Swiper:', error);
    }
  }

  /**
   * Open video popup at specific index
   */
  openVideoPopup(videoIndex) {
    this.currentVideoIndex = videoIndex || 0;
    const dialogComponent = this.querySelector('dialog-component');
    if (dialogComponent) {
      dialogComponent.showDialog();
    }
  }

  /**
   * Handle popup slide change
   * Using Swiper's slideChange event
   */
  handlePopupSlideChange() {
    if (!this.popupSwiper) {
      return;
    }

    // Check if dialog is open
    const dialog = this.querySelector('dialog');
    if (!dialog || !dialog.hasAttribute('open')) {
      return;
    }

    // Mute and pause all videos first
    this.muteAndPauseAllVideos();

    // Update current index
    this.currentVideoIndex = this.popupSwiper.activeIndex;

    // Unmute and play the active video
    setTimeout(() => {
      this.unmuteAndPlayCurrentVideo();
    }, 300); // Small delay to ensure slide transition completes
  }

  /**
   * Play current video in popup (initial load)
   */
  playCurrentVideo() {
    if (!this.popupSwiper) {
      return;
    }

    // Mute all videos first
    this.muteAndPauseAllVideos();

    // Unmute and play the active video
    this.unmuteAndPlayCurrentVideo();
  }

  /**
   * Unmute and play the current active video
   */
  unmuteAndPlayCurrentVideo() {
    if (!this.popupSwiper) {
      return;
    }

    const currentSlide = this.popupSwiper.slides[this.popupSwiper.activeIndex];
    if (!currentSlide) {
      return;
    }

    const videoWrapper = currentSlide.querySelector('.video-popup-video-wrapper');
    if (!videoWrapper) {
      return;
    }

    // Find deferred-media component
    const deferredMedia = videoWrapper.querySelector('deferred-media');
    if (deferredMedia) {
      // Load content if not already loaded
      if (!deferredMedia.getAttribute('data-media-loaded')) {
        if (typeof deferredMedia.loadContent === 'function') {
          deferredMedia.loadContent(false); // Don't focus
        }
      }

      // Wait a bit for content to load, then play and unmute
      setTimeout(() => {
        // Unmute the video element if it exists
        const videoElement = deferredMedia.querySelector('video');
        if (videoElement) {
          if(this.#isSoundOn){
            videoElement.muted = false;
            videoElement.closest('.video-popup-video-wrapper').classList.add('video-with-sound');
          }else{
            videoElement.muted = true;
            videoElement.closest('.video-popup-video-wrapper').classList.remove('video-with-sound');
          }
          videoElement.loop = true;
          videoElement.controls = false;
          videoElement.currentTime = 0;
        }

        // Unmute iframe (YouTube/Vimeo) by updating URL
        const iframeElement = deferredMedia.querySelector('iframe[data-video-type]');
        if (iframeElement && iframeElement.src) {
          this.unmuteIframe(iframeElement);
        }

        // Use theme's playMedia method
        if (typeof deferredMedia.playMedia === 'function') {
          deferredMedia.playMedia();
        }
      }, 100);
    } else {
      // Fallback: direct video/iframe elements (shouldn't happen with deferred-media)
      const videoElement = videoWrapper.querySelector('video');
      const iframeElement = videoWrapper.querySelector('iframe');

      if (videoElement) {
        if(this.#isSoundOn){
          videoElement.muted = false;
          videoElement.closest('.video-popup-video-wrapper').classList.add('video-with-sound');
        }else{
          videoElement.muted = true;
          videoElement.closest('.video-popup-video-wrapper').classList.remove('video-with-sound');
        }
        videoElement.closest('.video-popup-video-wrapper').classList.add('video-with-sound');
        videoElement.loop = true;
        videoElement.controls = false;
        videoElement.currentTime = 0;
        videoElement.play().catch(() => { });
      } else if (iframeElement) {
        this.unmuteIframe(iframeElement);
      }
    }
  }

  /**
   * Unmute iframe video (YouTube/Vimeo)
   */
  unmuteIframe(iframeElement) {
    if (!iframeElement || !iframeElement.src) {
      return;
    }

    try {
      const url = new URL(iframeElement.src);

      // For YouTube
      if (url.hostname.includes('youtube.com') || url.hostname.includes('youtu.be')) {
        url.searchParams.set('mute', '0');
        url.searchParams.set('controls', '0');
        iframeElement.src = url.toString();
      }
      // For Vimeo
      else if (url.hostname.includes('vimeo.com')) {
        url.searchParams.set('muted', '0');
        url.searchParams.set('controls', '0');
        iframeElement.src = url.toString();
      }
    } catch (e) {
      // Fallback for invalid URLs - use string manipulation
      let src = iframeElement.src;
      if (src.includes('youtube.com') || src.includes('youtu.be')) {
        src = src.replace(/[&?]mute=\d+/g, '');
        src = src.replace(/[&?]controls=\d+/g, '');
        src += (src.includes('?') ? '&' : '?') + 'mute=0&controls=0';
        iframeElement.src = src;
      } else if (src.includes('vimeo.com')) {
        src = src.replace(/[&?]muted=\d+/g, '');
        src = src.replace(/[&?]controls=\d+/g, '');
        src += (src.includes('?') ? '&' : '?') + 'muted=0&controls=0';
        iframeElement.src = src;
      }
    }
  }

  /**
   * Mute and pause all videos in popup
   */
  muteAndPauseAllVideos() {
    const popupSlides = this.querySelectorAll('.video-popup-slide');
    popupSlides.forEach((slide) => {
      // Use deferred-media's pauseMedia method if available
      const deferredMedia = slide.querySelector('deferred-media');
      if (deferredMedia && typeof deferredMedia.pauseMedia === 'function') {
        deferredMedia.pauseMedia();
      }

      // Also handle direct video elements
      const videoElement = slide.querySelector('video');
      if (videoElement) {
        videoElement.pause();
        videoElement.currentTime = 0;
        videoElement.muted = true;
        videoElement.controls = false;
      }

      // Mute iframes (YouTube/Vimeo)
      const iframeElement = slide.querySelector('iframe[data-video-type]');
      if (iframeElement && iframeElement.src) {
        try {
          const url = new URL(iframeElement.src);

          // For YouTube
          if (url.hostname.includes('youtube.com') || url.hostname.includes('youtu.be')) {
            url.searchParams.set('mute', '1');
            url.searchParams.set('controls', '0');
            iframeElement.src = url.toString();
          }
          // For Vimeo
          else if (url.hostname.includes('vimeo.com')) {
            url.searchParams.set('muted', '1');
            url.searchParams.set('controls', '0');
            iframeElement.src = url.toString();
          }
        } catch (e) {
          // Fallback for invalid URLs
          let src = iframeElement.src;
          if (src.includes('youtube.com') || src.includes('youtu.be')) {
            src = src.replace(/[&?]mute=\d+/g, '');
            src = src.replace(/[&?]controls=\d+/g, '');
            src += (src.includes('?') ? '&' : '?') + 'mute=1&controls=0';
            iframeElement.src = src;
          } else if (src.includes('vimeo.com')) {
            src = src.replace(/[&?]muted=\d+/g, '');
            src = src.replace(/[&?]controls=\d+/g, '');
            src += (src.includes('?') ? '&' : '?') + 'muted=1&controls=0';
            iframeElement.src = src;
          }
        }
      }
    });
  }

  /**
   * Pause all videos in popup (for dialog close)
   */
  pauseAllVideos() {
    const popupSlides = this.querySelectorAll('.video-popup-slide');
    popupSlides.forEach((slide) => {
      // Use deferred-media's pauseMedia method if available
      const deferredMedia = slide.querySelector('deferred-media');
      if (deferredMedia && typeof deferredMedia.pauseMedia === 'function') {
        deferredMedia.pauseMedia();
      }

      // Also handle direct video elements
      const videoElement = slide.querySelector('video');
      if (videoElement) {
        videoElement.pause();
        videoElement.currentTime = 0;
      }
    });
  }
}

// Register the custom element
customElements.define('product-video-slider', ProductVideoSlider);

// Export for module usage
export default ProductVideoSlider;

